import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSegmentViewEvents } from 'hooks/useSegmentView';
import Image from 'next/image';
import { Input } from '@acko-tech/building-blocks.ui.input';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import SnackBar from 'library/snackbar';
import { getImageBasePath } from 'utils/generics';
import { inputRegexValidation, phoneNumberValidation } from 'utils/validation';
import {
    SegmentEventNameTypes,
    SegmentEventPageTypes,
    SegmentEventPages,
    PRODUCT,
    SUB_PRODUCT_NAME,
    SegmentEventActionTypes,
    SegmentEventButtonNames,
    SegmentEventActionNames,
} from 'utils/segment/constants';
import { useViewport } from 'utils/responsive';
import { MPosContext } from 'utils/context';
import OTP from 'widgets/otp';
import * as Styles from './styles';
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics';

interface IProps {
    subDealers: any;
    setSubDealers: any;
    setShowAddNewSubDealer: any;
}

function AddNewSubDealer({
    subDealers,
    setSubDealers,
    setShowAddNewSubDealer,
}: IProps) {
    const { partnerId, userDetails, permissions } = useContext(MPosContext);

    const router = useRouter();

    const [phoneNumber, setPhoneNumber] = useState('');
    const [name, setName] = useState('');
    const [dealerCode, setDealerCode] = useState('');
    const [showOTP, setShowOTP] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [validationErr, setValidationErr] = useState({
        name: '',
        phoneNumber: '',
        dealerCode: '',
    });
    const { isMobile } = useViewport();
    const { group } = permissions;

    useSegmentViewEvents(SegmentEventNameTypes.VIEW, {
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        page_type: SegmentEventPageTypes.MAIN_PAGE,
        view_name: SegmentEventPages.VIEW_CONFIRM_SUB_DEALER,
        product: PRODUCT,
        sub_product_name: SUB_PRODUCT_NAME,
        platform: isMobile ? 'mweb' : 'web',
        referrer: document.referrer || '',
        device: navigator.userAgent || '',
        partner: partnerId,
    });

    useEffect(() => {
        let validationStatus = false;
        if (
            !(
                inputRegexValidation(phoneNumber) &&
                phoneNumberValidation(phoneNumber)
            )
        ) {
            validationStatus = true;
            if (phoneNumber) {
                setValidationErr({
                    ...validationErr,
                    phoneNumber: 'Invalid mobile number',
                });
            }
        } else {
            setValidationErr({
                ...validationErr,
                phoneNumber: '',
            });
        }
        if (!(inputRegexValidation(name) && name?.length > 2)) {
            validationStatus = true;
            if (name) {
                setValidationErr({
                    ...validationErr,
                    name: 'Invalid name',
                });
            }
        } else {
            setValidationErr({
                ...validationErr,
                name: '',
            });
        }
        if (dealerCode) {
            if (!(inputRegexValidation(dealerCode) && dealerCode?.length > 2)) {
                validationStatus = true;
                setValidationErr({
                    ...validationErr,
                    dealerCode: 'Invalid dealer code',
                });
            } else {
                setValidationErr({
                    ...validationErr,
                    dealerCode: '',
                });
            }
        }
        setIsDisabled(validationStatus);
    }, [phoneNumber, name, dealerCode]);

    const addDealer = () => {
        if (phoneNumber === userDetails.phone) {
            setSnackbar({
                type: 'failure',
                message:
                    'Sub dealer phone should not be a dealer`s phone number',
            });
            return;
        }
        const filteredList = subDealers?.filter(
            (subDealer) => subDealer?.phone === phoneNumber
        );
        if (filteredList?.length > 0) {
            setSnackbar({
                type: 'failure',
                message: 'Sub dealer phone number is already exists.',
            });
            return;
        }
        import('./apis').then(async (req) => {
            setIsLoading(true);
            const { status, errMsg } = await req.triggerSubDealerAddOTP(
                userDetails.phone
            );
            if (status) {
                setShowOTP(true);
            } else {
                setSnackbar({
                    type: 'failure',
                    message: errMsg || 'Oops. Something went wrong.',
                });
            }
            setIsLoading(false);
        });
        /**
         * Segment event
         */

        trackEvent(SegmentEventNameTypes.ACTION, {
            page_url: window?.location?.href || '',
            button_name: SegmentEventButtonNames.CONFIRM_SUB_DEALER,
            action_type: SegmentEventActionTypes.TAP_BTN,
            action_name: SegmentEventActionNames.CLICK_CONFIRM_SUB_DEALERS,
            product: PRODUCT,
            sub_product: SUB_PRODUCT_NAME,
            platform: isMobile ? 'mweb' : 'web',
            device: navigator.userAgent || '',
            partner: partnerId,
        });
    };

    const handleSnakbarComplete = () => {
        setSnackbar({
            type: '',
            message: '',
        });
    };

    const onSuccess = () => {
        import('../apis').then(async (req) => {
            setIsLoading(true);
            const { status, statusCode } = await req.addSubDealer(
                partnerId,
                [
                    {
                        name,
                        phone: phoneNumber,
                        partner_id: partnerId,
                        dealer_code: dealerCode,
                    },
                ],
                group
            );
            setIsLoading(false);
            if (statusCode === 401) {
                router.push(`/login`);
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            if (status) {
                setSubDealers((subDealers: any) =>
                    subDealers.concat([{ phone: phoneNumber, name }])
                );
                setName('');
                setPhoneNumber('');
                setDealerCode('');
                setShowAddNewSubDealer(false);
                setSnackbar({
                    type: 'success',
                    message: 'Sub-dealer successfully added',
                });
                return;
            }
            setSnackbar({
                type: 'failure',
                message: 'Oops. Something went wrong.',
            });
        });
    };

    const onBack = () => {
        setShowAddNewSubDealer(false);
    };

    return (
        <Styles.Wrapper>
            {showOTP ? (
                <OTP
                    phoneNumber={userDetails.phone}
                    onClickBack={() => setShowOTP(false)}
                    onClickEdit={() => setShowOTP(false)}
                    onSuccess={onSuccess}
                />
            ) : (
                <>
                    <Styles.Header>
                        <Styles.BackArrowIcon>
                            <Image
                                width={'10px'}
                                height={'24px'}
                                alt={''}
                                src={getImageBasePath('/icons/backArrow.svg')}
                                onClick={onBack}
                            />
                        </Styles.BackArrowIcon>
                    </Styles.Header>
                    <Styles.Title>Add Sub-dealer</Styles.Title>
                    <Styles.InputWrapper>
                        <Input
                            name={'phone'}
                            type={'text'}
                            className={''}
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            placeHolder={'Enter mobile number'}
                            leftElement={'+91'}
                            maxLength={'10'}
                            hasError={validationErr.phoneNumber.length > 0}
                            errMsg={validationErr.phoneNumber}
                        />
                    </Styles.InputWrapper>
                    <Styles.InputWrapper>
                        <Input
                            name={'name'}
                            type={'text'}
                            className={''}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeHolder={'Enter name'}
                            hasError={validationErr.name.length > 0}
                            errMsg={validationErr.name}
                        />
                    </Styles.InputWrapper>
                    <Styles.InputWrapper>
                        <Input
                            name={'dealer_code'}
                            type={'text'}
                            className={''}
                            value={dealerCode}
                            onChange={(e) => setDealerCode(e.target.value)}
                            placeHolder={`Enter your dealer code`}
                        />
                    </Styles.InputWrapper>
                    <Styles.AddButtonWrapper>
                        <PrimaryButton
                            loading={isLoading}
                            disabled={isDisabled}
                            onClick={addDealer}
                        >
                            Add dealer
                        </PrimaryButton>
                    </Styles.AddButtonWrapper>
                </>
            )}
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={handleSnakbarComplete}
            />
        </Styles.Wrapper>
    );
}

export default AddNewSubDealer;
